// How a project's components were found, in words. The scope sync stores codes in each row's
// `componentSource` (predict_scope/from_sbom.py: installed, vendor-sbom, none) and the watch list sends
// `vendor-product` for a vendor's own product; this is the one place they become words. A vendor's SBOM
// reads as the vendor's own name, "F5 SBOM", so no vendor needs words of its own anywhere.

const WORDS = {
  installed: () => 'your CMDB',
  'vendor-sbom': vendorName => `${vendorName || 'Vendor'} SBOM`,
  none: () => 'no SBOM',
  'vendor-product': () => 'vendor product'
};

/** One code in words, for the vendor named `vendorName`. A value that isn't a code shows as it is. */
export const sourceWords = (code, vendorName) => (WORDS[code] ? WORDS[code](vendorName) : code);

/** A row's `componentSource` ("installed, vendor-sbom") as its codes. */
export const sourceCodes = componentSource =>
  typeof componentSource === 'string' ? componentSource.split(', ').filter(Boolean) : [];

// The codes a page treats specially: a host's own packages (shown as the best source) and a
// release with no SBOM (shown neutral).
export const INSTALLED = 'installed';
export const NO_SBOM = 'none';

// A product's `originSources` (from_sbom.origin_sources, STRY0039142) in words: one line per source with
// how many hosts it covers, what it found and, for hosts with none, why. Every word comes from a code and
// the vendor's name, so the page names no vendor and no product.
const UNITS = {
  host: ['host', 'hosts'],
  device: ['device', 'devices'],
  node: ['node', 'nodes'],
  cluster: ['cluster', 'clusters']
};
const OF_WORDS = { packages: 'installed packages', addOns: 'running add-ons' };
const REASONS = {
  noVersion: () => 'no version recorded in your CMDB',
  unidentified: () => 'its software can’t be told from your CMDB',
  beforeFloor: vendor => `older than the first release ${vendor} published an SBOM for`,
  notListed: vendor => `${vendor} publishes no SBOM for this release`,
  notCovered: vendor => `${vendor} publishes no SBOM for this software`,
  unparsedVersion: () => 'not a release version (a pre-release, say)'
};
const NAMES_SHOWN = 3;

/** `n` and its unit, singular or plural ("1 node", "12 nodes"); an unknown unit reads as hosts. */
export const countWords = (n, unit) => {
  const [one, many] = UNITS[unit] || UNITS.host;
  return `${n} ${n === 1 ? one : many}`;
};

const reasonWords = (code, vendor) => (REASONS[code] ? REASONS[code](vendor || 'The vendor') : 'no SBOM found');
const sentence = text => text.charAt(0).toUpperCase() + text.slice(1);
const listNames = names =>
  names.length > NAMES_SHOWN
    ? `${names.slice(0, NAMES_SHOWN).join(', ')} +${names.length - NAMES_SHOWN} more`
    : names.join(', ');

/**
 * [{code, source, count, found}] for a product's stored `originSources`, worded for `vendorName`.
 * `found` names the OSS a source put in scope (`projects`: {code: [names]}), or says why hosts have none.
 */
export const breakdownLines = (originSources, vendorName, projects = {}) => {
  const unit = originSources?.unit;
  return (originSources?.sources || [])
    .filter(entry => entry && entry.hosts > 0)
    .map(entry => {
      const names = projects[entry.code] || [];
      if (entry.code === NO_SBOM) {
        const reasons = Object.entries(entry.reasons || {}).sort((a, b) => b[1] - a[1]);
        return {
          code: entry.code,
          source: 'No SBOM',
          count: countWords(entry.hosts, unit),
          found: reasons.length
            ? sentence(
                reasons
                  .map(([code, n]) =>
                    reasons.length > 1 ? `${reasonWords(code, vendorName)} (${n})` : reasonWords(code, vendorName)
                  )
                  .join('; ')
              )
            : ''
        };
      }
      const nearest = entry.nearest ? `${countWords(entry.nearest, unit)} by the nearest release` : '';
      return {
        code: entry.code,
        source:
          entry.code === INSTALLED
            ? `Your CMDB${OF_WORDS[entry.of] ? `, ${OF_WORDS[entry.of]}` : ''}`
            : sourceWords(entry.code, vendorName),
        count: entry.groups
          ? `${entry.groups} of ${countWords(entry.groupsOf, entry.groupUnit)}`
          : countWords(entry.hosts, unit),
        found: [names.length ? listNames(names) : 'No OSS Predict tracks yet', nearest].filter(Boolean).join(' · ')
      };
    });
};
